<template>
  <div>
    <AlertBox v-if="!canViewSpeakers" type="warning" class="mt-5" data-testid="speaker-no-company">
      {{ t('PartnerCompany.noPartnerCompany') }}
    </AlertBox>
    <div v-if="canViewSpeakers">
      <div class="pt-2 pb-5">
        <h1 data-testid="speaker-view-heading">{{ t('Speakers.assignEvent') }}</h1>

        <BackLink class="mt-4" data-testid="speaker-view-back-link" :to="{ name: 'speakerList' }">{{ t('backToOverview') }}</BackLink>

        <div>
          <SpeakerEventsForm
            :show-confirm-modal="true"
            :title="t('SpeakerEventsForm.headline')"
            :speaker="speaker"
            :is-uploading="loading"
            @submit-form="save"
          >
            <template #navHeader>
              <div class="relative pt-6">
                <div class="align-start flex">
                  <img :src="speaker.photoUrl || ''" class="mr-6 mb-8 h-[160px]" />
                  <div>
                    <p class="text-headline-m">{{ speaker.name }}</p>
                    <p class="mt-2 mb-4 text-body-s text-main">
                      {{ speaker.partnerCompanyName }} <span v-if="speaker.title">· {{ speaker.title }}</span>
                    </p>
                    <div>
                      <div v-for="interest in speaker.interests" :key="interest.id" class="mr-2 chip bg-surface-inverse">
                        {{ interest.text }}
                      </div>
                    </div>
                    <div class="mt-6">
                      <a
                        v-if="speaker.linkedinUrl"
                        target="_blank"
                        class="mr-5 inline-block"
                        :href="speaker.linkedinUrl"
                        data-testid="speaker-view-linkedin-link"
                        ><img :src="`/assets/images/linkedin-icon.svg`"
                      /></a>
                      <a
                        v-if="speaker.twitterUrl"
                        target="_blank"
                        class="mr-5 inline-block"
                        :href="speaker.twitterUrl"
                        data-testid="speaker-view-twitter-link"
                        ><img :src="`/assets/images/twitter-icon.svg`"
                      /></a>
                      <a
                        v-if="speaker.instagramUrl"
                        target="_blank"
                        class="mr-5 inline-block"
                        :href="speaker.instagramUrl"
                        data-testid="speaker-view-instagram-link"
                        ><img :src="`/assets/images/instagram-icon.svg`"
                      /></a>
                      <a
                        v-if="speaker.websiteUrl"
                        target="_blank"
                        class="mr-5 inline-block"
                        :href="speaker.websiteUrl"
                        data-testid="speaker-view-website-link"
                        ><img :src="`/assets/images/web-icon.svg`"
                      /></a>
                    </div>
                    <p class="mt-5 pb-8">{{ speakerDescription }}</p>
                  </div>
                </div>
                <div class="absolute top-6 right-0 text-right">
                  <p>{{ t('Speaker.falseInfo') }}</p>
                  <a href="mailto:exhibitors@omr.com" data-testid="speaker-view-contact-link">{{ t('Speaker.contactUs') }}</a>
                </div>
              </div>
            </template>
          </SpeakerEventsForm>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { type Speaker, useCreateEventSpeakerMutation, useDeleteEventSpeakerMutation } from '@/gql/myomr'
import { showNotification } from '@/helpers/notificationHelper'
import { useRoleRedirect } from '@/services/roleRedirect'
import { $can } from '@/services/roles'

useRoleRedirect('viewSpeakers')

const router = useRouter()

const { t, locale } = useI18n()

const canViewSpeakers = computed(() => {
  return $can('viewSpeakers', { for: 'User' })
})

const props = defineProps<{
  speaker: Speaker
}>()

const emit = defineEmits(['refetch'])

const speakerDescription = computed(() => {
  return locale.value === 'de' ? props.speaker.descriptionDe : props.speaker.descriptionEn
})

const { mutate: createEventSpeaker, loading: createEventSpeakerLoading } = useCreateEventSpeakerMutation()
const { mutate: deleteEventSpeaker, loading: deleteEventSpeakerLoading } = useDeleteEventSpeakerMutation()
const loading = computed(() => createEventSpeakerLoading.value || deleteEventSpeakerLoading.value)

async function save(eventSpeakerData: { eventsToAdd: Array<string>; eventSpeakersToRemove: Array<string> }) {
  let hasErrors = false
  try {
    await Promise.all(
      eventSpeakerData.eventsToAdd.map((eventId) =>
        createEventSpeaker({
          eventId: eventId,
          speakerId: props.speaker.id,
        }),
      ),
    )
  } catch (_err) {
    hasErrors = true
    showNotification(t('Speakers.notifications.eventsCreationIssue'), 'error')
  }

  try {
    await Promise.all(
      eventSpeakerData.eventSpeakersToRemove.map((eventSpeakerId) =>
        deleteEventSpeaker({
          eventSpeakerId,
        }),
      ),
    )
  } catch (_err) {
    hasErrors = true
    showNotification(t('Speakers.notifications.eventsRemovalIssue'), 'error')
  }

  if (!hasErrors) showNotification(t('Speaker.successfullySaved'), 'success')
  window.scrollTo(0, 0)
  emit('refetch')
  router.push({ name: 'speakerList' })
}
</script>
